import { create } from "zustand";
import { parseWorkflowToSteps, type StepDefinition } from "../parser/workflowParser";
import { serializeStepsToWorkflow } from "../parser/workflowSerializer";
import { validateWorkflow, type ValidationError } from "../parser/workflowValidator";
import type { ServerWorkflow, WorkflowUpdatePayload } from "../parser/types";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { ActionNodeData } from "../nodes/ActionNode";
import { Trigger, type TriggerType } from "../nodes/TriggerNode";

export type ConditionKind = "WhenToContinue";

export type ActionKind =
    | "AutomatedResponse"
    | "NotifyUser"
    | "CreateLead"
    | "ApplyTag"
    | "AddNote"
    | "LogActivity";

const CONDITION_KIND_TITLES: Record<ConditionKind, string> = {
    WhenToContinue: "When to continue",
};

const ACTION_KIND_TITLES: Record<ActionKind, string> = {
    AutomatedResponse: "Automated Response",
    NotifyUser: "Notify User",
    CreateLead: "Create Lead",
    ApplyTag: "Apply Tag",
    AddNote: "Add Note",
    LogActivity: "Log Activity",
};

function getNextNumericNodeId(steps: StepDefinition[]): string {
    let max = 0;
    for (const s of steps) {
        const m = /^n(\d+)$/i.exec(s.id);
        if (m) max = Math.max(max, parseInt(m[1], 10));
    }
    return `n${max + 1}`;
}

function makeDefaultTriggerStep(triggerType: TriggerType): StepDefinition {
    return {
        id: "n1",
        type: "trigger",
        data:
            triggerType === Trigger.Message.Outbound
                ? {
                      triggerType: Trigger.Message.Outbound,
                      title: "Outgoing",
                      description: "When an Outgoing Message Is Sent",
                  }
                : {
                      triggerType: Trigger.Message.Inbound,
                      title: "Incoming",
                      description: "When a message comes in",
                  },
    };
}

export interface SaveWorkflowResult {
    success: boolean;
    validationErrors: ValidationError[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    serverResponse?: any;
}

export interface WorkflowStore {
    steps: StepDefinition[];
    workflowName: string;
    triggerType: TriggerType;

    workflowId: string | null;
    revision: number | null;
    serverStatus: string | null;
    applyOncePerContact: boolean | null;

    selectedNodeId: string | null;
    measuredHeights: Record<string, number>;
    closeSignal: number;
    /** Snapshot for dirty check: JSON of { steps, workflowName } when last loaded/saved */
    originalSnapshot: string | null;

    addStep: (insertIndex: number, nodeType: "condition" | "action") => void;
    deleteStep: (stepId: string) => void;
    updateStepData: (stepId: string, data: Partial<ConditionNodeData> | Partial<ActionNodeData>) => void;
    setWorkflowName: (name: string) => void;
    setApplyOncePerContact: (value: boolean) => void;
    setServerStatus: (status: string | null) => void;

    selectNode: (nodeId: string | null) => void;
    incrementCloseSignal: () => void;
    updateMeasuredHeights: (changes: Array<{ id: string; height: number }>) => void;

    loadFromServer: (serverWorkflow: ServerWorkflow) => void;
    initNewWorkflow: (triggerType: TriggerType) => void;
    discardChanges: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    saveWorkflow: (deps: { fetchData: any; appProperties: any }) => Promise<SaveWorkflowResult>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setWorkflowEnabled: (deps: { fetchData: any; appProperties: any }, enabled: boolean) => Promise<{ success: boolean }>;
    clearStore: () => void;
}

const INITIAL_STATE = {
    steps: [] as StepDefinition[],
    workflowName: "Untitled 1",
    triggerType: Trigger.Message.Inbound as TriggerType,
    workflowId: null as string | null,
    revision: null as number | null,
    serverStatus: null as string | null,
    applyOncePerContact: null as boolean | null,
    selectedNodeId: null as string | null,
    measuredHeights: {} as Record<string, number>,
    closeSignal: 0,
    originalSnapshot: null as string | null,
};

function snapshotState(
    steps: StepDefinition[],
    workflowName: string,
    applyOncePerContact: boolean | null
): string {
    return JSON.stringify({ steps, workflowName, applyOncePerContact });
}

export const useWorkflowStore = create<WorkflowStore>()((set, get) => ({
    ...INITIAL_STATE,
    steps: [makeDefaultTriggerStep(Trigger.Message.Inbound)],

    addStep: (insertIndex, nodeType) => {
        const { triggerType } = get();
        if (triggerType === Trigger.Message.Outbound) return;
        set((state) => {
            const next = [...state.steps];
            const newId = getNextNumericNodeId(next);
            next.splice(insertIndex, 0, {
                id: newId,
                type: nodeType,
                data: { isPlaceholder: true },
            });
            return { steps: next, selectedNodeId: newId };
        });
    },

    deleteStep: (stepId) => {
        const { triggerType } = get();
        if (triggerType === Trigger.Message.Outbound) return;
        set((state) => {
            const step = state.steps.find((s) => s.id === stepId);
            if (!step || step.type === "trigger") return state;
            return {
                steps: state.steps.filter((s) => s.id !== stepId),
                selectedNodeId: null,
            };
        });
    },

    updateStepData: (stepId, data) => {
        set((state) => ({
            steps: state.steps.map((s) => (s.id === stepId ? { ...s, data: { ...s.data, ...data } } : s)),
        }));
    },

    setWorkflowName: (name) => {
        set({ workflowName: name });
    },

    setApplyOncePerContact: (value) => {
        set({ applyOncePerContact: value });
    },

    setServerStatus: (status) => {
        set({ serverStatus: status });
    },

    selectNode: (nodeId) => {
        set({ selectedNodeId: nodeId });
    },

    incrementCloseSignal: () => {
        set((state) => ({ closeSignal: state.closeSignal + 1 }));
    },

    updateMeasuredHeights: (changes) => {
        set((state) => {
            let changed = false;
            const next = { ...state.measuredHeights };
            for (const { id, height } of changes) {
                if (Math.abs((next[id] ?? 0) - height) > 1) {
                    next[id] = height;
                    changed = true;
                }
            }
            if (!changed) return {};
            return { measuredHeights: next };
        });
    },

    loadFromServer: (serverWorkflow) => {
        const parsedSteps = parseWorkflowToSteps(serverWorkflow);
        const isOutbound = serverWorkflow.triggerType?.toUpperCase().includes("OUTBOUND") ?? false;
        const triggerTypeFromServer: TriggerType = isOutbound ? Trigger.Message.Outbound : Trigger.Message.Inbound;
        const workflowName = serverWorkflow.name ?? "Untitled 1";
        const applyOncePerContact = serverWorkflow.applyOncePerContact ?? null;

        if (parsedSteps.length === 0) {
            const defaultTriggerStep = makeDefaultTriggerStep(triggerTypeFromServer);
            const emptySteps = [defaultTriggerStep];
            set({
                ...INITIAL_STATE,
                steps: emptySteps,
                workflowId: serverWorkflow.workflowId ?? null,
                workflowName,
                triggerType: triggerTypeFromServer,
                revision: serverWorkflow.revision ?? null,
                serverStatus: serverWorkflow.status ?? null,
                applyOncePerContact,
                selectedNodeId: null,
                measuredHeights: {},
                originalSnapshot: snapshotState(emptySteps, workflowName, applyOncePerContact),
            });
            return;
        }

        set({
            steps: parsedSteps,
            workflowId: serverWorkflow.workflowId ?? null,
            workflowName,
            triggerType: triggerTypeFromServer,
            revision: serverWorkflow.revision ?? null,
            serverStatus: serverWorkflow.status ?? null,
            applyOncePerContact,
            selectedNodeId: null,
            measuredHeights: {},
            originalSnapshot: snapshotState(parsedSteps, workflowName, applyOncePerContact),
        });
    },

    initNewWorkflow: (triggerType) => {
        const steps = [makeDefaultTriggerStep(triggerType)];
        set({
            ...INITIAL_STATE,
            triggerType,
            steps,
            originalSnapshot: snapshotState(steps, "Untitled 1", null),
        });
    },

    discardChanges: () => {
        const state = get();
        set({ originalSnapshot: snapshotState(state.steps, state.workflowName, state.applyOncePerContact) });
    },

    saveWorkflow: async ({ fetchData, appProperties }) => {
        const { steps, workflowName, triggerType, workflowId, applyOncePerContact, serverStatus, revision } = get();

        const validationErrors = validateWorkflow(steps, workflowName);
        if (validationErrors.length > 0) {
            return { success: false, validationErrors };
        }

        const payload = serializeStepsToWorkflow(steps, {
            workflowId: workflowId ?? undefined,
            name: workflowName,
            triggerType: triggerType,
            applyOncePerContact: applyOncePerContact ?? undefined,
            status: serverStatus ?? undefined,
            revision: revision ?? undefined,
        });

        const isUpdate = "workflowId" in payload && !!(payload as WorkflowUpdatePayload).workflowId;
        const apiUrl = isUpdate ? `workflows/${(payload as WorkflowUpdatePayload).workflowId}` : "workflows";
        const httpMethod = isUpdate ? "PUT" : "POST";
        const rawApiResponse = await fetchData(apiUrl, httpMethod, payload, appProperties);

        if (rawApiResponse == null) {
            return { success: false, validationErrors: [], serverResponse: rawApiResponse };
        }

        const parsedResponse = typeof rawApiResponse === "string" ? JSON.parse(rawApiResponse) : rawApiResponse;
        const responseEntity = parsedResponse?.data?.data ?? parsedResponse?.data ?? parsedResponse;

        const hasErrorField =
            responseEntity &&
            typeof responseEntity === "object" &&
            "error" in responseEntity &&
            (responseEntity as Record<string, unknown>).error != null;
        if (hasErrorField) {
            return { success: false, validationErrors: [], serverResponse: rawApiResponse };
        }

        if (responseEntity?.workflowId && !isUpdate) {
            set({
                workflowId: responseEntity.workflowId,
                revision: responseEntity.revision ?? 1,
                serverStatus: responseEntity.status ?? "DRAFT",
            });
        }
        set((state) => ({
            originalSnapshot: snapshotState(state.steps, state.workflowName, state.applyOncePerContact),
        }));

        return { success: true, validationErrors: [], serverResponse: rawApiResponse };
    },

    setWorkflowEnabled: async ({ fetchData, appProperties }, enabled) => {
        const { workflowId } = get();
        if (!workflowId) {
            return { success: false };
        }
        const endpoint = enabled ? "enable" : "disable";
        const serverResponse = await fetchData(
            `workflows/${workflowId}/${endpoint}`,
            "POST",
            null,
            appProperties
        );
        const data =
            serverResponse && typeof serverResponse === "object" && "data" in serverResponse
                ? (serverResponse as { data?: { status?: string } }).data
                : undefined;
        const nextStatus = data?.status ?? (enabled ? "ACTIVE" : "DISABLED");
        set({ serverStatus: nextStatus });
        return { success: true };
    },

    clearStore: () => {
        const steps = [makeDefaultTriggerStep(Trigger.Message.Inbound)];
        set({ ...INITIAL_STATE, steps, originalSnapshot: snapshotState(steps, "Untitled 1", null) });
    },
}));
export { CONDITION_KIND_TITLES, ACTION_KIND_TITLES };
export type { StepDefinition, ConditionNodeData, ActionNodeData, TriggerType, ServerWorkflow, ValidationError };
